import { useQuery, UseQueryResult, UseQueryOptions, QueryKey } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";
import { apiFetch } from "./apiFetch";

export class FetchError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "FetchError";
    this.status = status;
  }
}

export type FetchQueryParams<TQueryResult, TData, TError = Error, TPayload = void> = {
  queryKey: QueryKey;
  url: string;
  requestHeaders?: HeadersInit;
  options?: Omit<UseQueryOptions<TQueryResult, TError, TData>, "queryKey" | "queryFn">;
  method?: RequestInit["method"];
  skipAuthHeaders?: boolean;
  body?: TPayload;
  /**
   * Extra `fetch` options (e.g. `credentials`, `mode`, `cache`, `signal`) spread
   * onto the underlying request. `method`, `body`, and `headers` are managed by
   * this util and therefore excluded.
   */
  requestInit?: Omit<RequestInit, "method" | "body" | "headers">;
  getAccessToken?: () => Promise<string>;
};

// returns query options
export function getFetchQuery<TQueryResult, TData = TQueryResult, TError = Error, TPayload = void>({
  queryKey,
  url,
  options = {},
  requestHeaders = {},
  method = "GET",
  skipAuthHeaders = false,
  body,
  requestInit,
  getAccessToken,
}: FetchQueryParams<TQueryResult, TData, TError, TPayload>): UseQueryOptions<TQueryResult, TError, TData> {
  const queryFn = async () => {
    let authToken: string | undefined;
    if (!skipAuthHeaders && getAccessToken) authToken = await getAccessToken();
    const response = await apiFetch(url, {
      method,
      headers: {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        "X-REQUEST-ID": uuidv4(),
        "Content-Type": "application/json",
        ...requestHeaders,
      },
      body: body ? JSON.stringify(body) : undefined,
      ...requestInit,
    });
    if (!response.ok) {
      const errorResponse = (await response.json()) as { message: string } | undefined;
      throw new FetchError(errorResponse?.message || response.statusText, response.status);
    }
    return response.json() as Promise<TQueryResult>;
  };
  return { queryKey, queryFn, ...options };
}

// returns hook to use in component
export function useFetchQuery<TQueryResult, TData = TQueryResult, TError = Error, TPayload = void>(
  params: Omit<FetchQueryParams<TQueryResult, TData, TError, TPayload>, "getAccessToken">,
): UseQueryResult<TData, TError> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const queryOptions = getFetchQuery<TQueryResult, TData, TError, TPayload>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}
